import { actionCreators } from 'app/area/risk-matrix-administration/actions';
import { mdResolution, RiskMatrixIds, RiskMatrixTranslation, riskMatrixTranslation } from 'app/area/risk-matrix-administration/constants';
import { RiskMatrixType } from 'app/area/risk-matrix-administration/components/type/risk-matrix-type';
import { withOptions } from 'app/components/options-handler';
import { STATE } from 'app/constants';
import { containerFactory } from 'app/infrastructure/redux';
import React, { Component } from 'react';
import { ElementLoader, InfoText, InputLabel, Paper, PromptConfirmation } from 'ui';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { PromptProps, withPrompt } from 'app/components';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { RiskMatrixState } from 'app/area/risk-matrix-administration/types-store';
import { Options, SetFirstTypeAsDefault } from 'app/area/risk-matrix-administration/types';
import styles from './risk-matrix-administration.scss';
import { ListRendererProps } from 'app/area/codetable/list/list-renderer/types';
import { RiskMatrixStickyHeader } from 'app/area/risk-matrix-administration/components/sticky-header/risk-matrix-sticky-header';
import { ControlPanel } from 'app/area/risk-matrix-administration/components/control-panel/control-panel';
import { CaseTypes } from 'app/area/risk-matrix-administration/components/case-types/case-types';
import { endpoints } from 'app/area/risk-matrix-administration/endpoints';
import { Matrix } from 'app/area/risk-matrix-administration/components/matrix/matrix';
import { ThirdAxisItemPicker } from './components/third-axis-item-picker/third-axis-item-picker';
import { CodetableAccessState } from 'app/area/codetable/types-store';
import { getAxisDescription, mapToRiskMatrixDefinition } from 'app/area/risk-matrix-administration/mappers/map-to-risk-matrix-definition';
import { deepEquals } from 'app/infrastructure/js';
import { ResizeWrapper } from 'app/components/resize-wrapper';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';

type RiskMatrixAdministrationProps = {
  breadcrumbs: number[];
  options: Options; loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  translation: RiskMatrixTranslation;
  access: CodetableAccessState;
  width: number;
} & typeof actionCreators & RiskMatrixState & ListRendererProps & PromptProps;

interface RiskMatrixAdministrationState {
  shouldDisplayInternalModal: boolean;
  thirdAxisItemPending: ComplexValue<number>;
  typePending: ComplexValue<number>;
}

class RiskMatrixAdministrationComponent extends Component<RiskMatrixAdministrationProps, RiskMatrixAdministrationState> {
  public static statePath: string = STATE.riskMatrix;

  public state: Readonly<RiskMatrixAdministrationState> = {
    shouldDisplayInternalModal: false,
    thirdAxisItemPending: undefined,
    typePending: undefined
  };

  public componentDidMount() {
    this.loadAllTypes();
    this.props.clearSelectedCell();
    this.props.clearThirdAxisItem();
    this.props.clearModifiedCells();
  }

  public componentDidUpdate(previousProps: RiskMatrixAdministrationProps) {
    const {
      details, options, type, previousType, loadAxes, loadCells, loadDetails, loadRiskAreas, setType, axes,
      fetchingAxes, fetchingCells, fetchingDetails, fetchingRiskAreas, fetchingCaseTypes, clearSelectedCell, thirdAxisItem,
      loadCaseTypes, setPreviousType, clearModifiedCells, clearThirdAxisItem
    } = this.props;

    const { details: previousDetails, options: previousOptions,
      thirdAxisItem: previousThirdAxisItem, axes: previousAxes } = previousProps;

    this.setFirstTypeAsDefault({ previousOptions, options, type, setType, previousType });

    if (this.hasComplexValueChanged(previousType, type)) {
      clearSelectedCell();
      clearModifiedCells();
      setPreviousType(type);
      clearThirdAxisItem();
      !fetchingDetails && type && loadDetails(type);
      !fetchingCaseTypes && type && loadCaseTypes(type);
      !fetchingRiskAreas && type && loadRiskAreas();
      !fetchingCells && type && loadCells(type);
    }

    if (this.hasComplexValueChanged(previousThirdAxisItem, thirdAxisItem)) {
      clearSelectedCell();
      clearModifiedCells();
      !fetchingCells && previousThirdAxisItem && type && loadCells(type);
    }

    if (this.hasChangedAndIsValid(previousDetails, details)) {
      !fetchingAxes && loadAxes(details);
    }

    if (this.hasChangedAndIsValid(previousAxes, axes)) {
      type && this.setThirdAxisItemAsDefault();
    }
  }

  public componentWillUnmount(): void {
    this.props.clearAllData();
  }

  private is3DMatrix = () => this.props.axes?.length === 3;

  private loadAllTypes = () => this.props.loadOptions(RiskMatrixIds.riskMatrixType,
    endpoints.types(),
    {
      siblingId: undefined,
      levels: undefined
    });

  private setFirstTypeAsDefault = ({ options, setType, type, previousOptions = {}, previousType }: SetFirstTypeAsDefault) =>
    !previousType && !isDefinedIncludingZero(type?.value) && !previousOptions?.riskMatrixType?.codes &&
    options?.riskMatrixType?.codes?.length > 0 && setType(options?.riskMatrixType.codes[0]);

  private setThirdAxisItemAsDefault = () => this.is3DMatrix() && !this.props.thirdAxisItem &&
    !!this.props.axes?.[2]?.items?.[0] && !this.props.thirdAxisItem && this.setThirdAxisItem(({
      ...this.props.axes[2].items[0], value: this.props.axes[2].items[0].id
    }));

  private hasComplexValueChanged = (previousValue: ComplexValue<number>, value: ComplexValue<number>) =>
    previousValue !== value && (isDefinedIncludingZero(value?.value) || isDefinedIncludingZero(previousValue?.value));

  private hasChangedAndIsValid = (previousObject: object, object: object) =>
    previousObject !== object && !!object;

  private isRiskMatrixReady = () => !this.isRiskMatrixFetching() && this.props.axes && this.props.cells && this.props.details &&
    this.props.riskAreas && this.props.caseTypes;

  private isRiskMatrixFetching = () => this.props.fetchingAxes || this.props.fetchingCells || this.props.fetchingDetails ||
    this.props.fetchingRiskAreas || this.props.fetchingCaseTypes;

  private chooseCell = (xAxisItemIndex: number, yAxisItemIndex: number) => {
    if (this.isReadOnlyMode()) {
      return;
    }

    this.removeCellFocus();

    this.props.selectCell({
      xAxisItemIndex, yAxisItemIndex, axes: this.props.axes, cells: this.props.cells, thirdAxisItem: this.props.thirdAxisItem
    });
  };

  private removeCellFocus = () => {
    (document.activeElement as HTMLElement).blur();
  };

  private updateSelectedCellRiskWeight = (riskWeight: number = 0) =>
    this.props.updateSelectedCell({
      patch: { riskWeight },
      selectedCellId: this.props.selectedCellId,
      cells: this.props.cells,
      modifiedCells: this.props.modifiedCells
    });

  private updateSelectedCellRiskArea = (riskArea: ComplexValue<number>) =>
    this.props.updateSelectedCell({
      patch: { riskArea: riskArea.value },
      selectedCellId: this.props.selectedCellId,
      cells: this.props.cells,
      modifiedCells: this.props.modifiedCells
    });

  private cancelChanges = () => {
    this.isAnyCellModified() && this.setState({ shouldDisplayInternalModal: true });
  };

  private saveChanges = () => this.props.saveCells(this.props.modifiedCells, this.props.translation.risk_matrix_saved_successfully,
    this.props.type);

  private setThirdAxisItem = (thirdAxisItem: ComplexValue<number>) => {
    if (!this.isAnyCellModified()) {
      this.props.setThirdAxisItem(thirdAxisItem);
      return;
    }

    this.setState({ shouldDisplayInternalModal: true, thirdAxisItemPending: thirdAxisItem });
  };

  private setType = (type: ComplexValue<number>) => {
    if (!this.isAnyCellModified()) {
      this.props.setType(type);
      return;
    }

    this.setState({ shouldDisplayInternalModal: true, typePending: type });
  };

  private onInternalModalContinue = () => {
    this.state.thirdAxisItemPending && this.props.setThirdAxisItem(this.state.thirdAxisItemPending);
    this.state.typePending && this.props.setType(this.state.typePending);
    this.props.clearModifiedCells();

    this.setState({ shouldDisplayInternalModal: false, thirdAxisItemPending: undefined, typePending: undefined });
  };

  private onInternalModalCancel = () => this.setState({
    shouldDisplayInternalModal: false,
    thirdAxisItemPending: undefined,
    typePending: undefined
  });

  private getSelectedCell = () => {
    const { cells, modifiedCells, selectedCellId } = this.props;

    if (!isDefinedIncludingZero(selectedCellId)) {
      return undefined;
    }

    return modifiedCells.find(cell => cell.id === selectedCellId) ?? cells.find(cell => cell.id === selectedCellId);
  };

  private getMatrixCells = () => {
    const { cells, modifiedCells } = this.props;

    return cells.map(cell => modifiedCells.find(modifiedCell => modifiedCell.id === cell.id) ?? cell);
  };

  private isReadOnlyMode = () => !this.props.access?.data?.codetables?.create;

  private isTypeChosen = () => isDefinedIncludingZero(this.props.type?.value);

  private areNoTypesAvailable = () => Array.isArray(this.props.options?.riskMatrixType?.codes) &&
    this.props.options?.riskMatrixType?.codes.length === 0;

  private isAnyCellModified = () => selectors.shouldPrompt(this.props);

  private isMobile = (width: number) => width <= mdResolution;

  // eslint:disable-next-line: max-func-body-length
  public render() {
    const { options, loadOptions, translation, type, axes, riskAreas, topToolbar, thirdAxisItem, caseTypes,
      fetchingCaseTypes, selectedCellId, clearSelectedCell, width } = this.props;

    const selectedCell = this.getSelectedCell();
    const matrixCells = this.getMatrixCells();
    const readOnly = this.isReadOnlyMode();
    const thirdAxisLabel = getAxisDescription(axes?.[2]?.codetableId, translation);
    const riskMatrixTypeOptions = options?.riskMatrixType?.codes ?? [];
    const fetchingRiskMatrixType = options?.riskMatrixType?.fetching;
    const shouldHideCaseTypes = riskMatrixTypeOptions.length === 1 || this.props.fetchingCaseTypes;
    const riskMatrixDefinition = this.isRiskMatrixReady() ? mapToRiskMatrixDefinition({
      axes, cells: matrixCells, riskAreas, translation, thirdAxisItem, selectedCellId
    }) : undefined;
    const areThirdAxisItemCellsMissing = this.isRiskMatrixReady() ?
      riskMatrixDefinition?.cells?.length > 0 && riskMatrixDefinition.axes?.length === 3 &&
      isDefinedIncludingZero(thirdAxisItem?.value) &&
      !riskMatrixDefinition?.cells.find(cell => cell.axisValues?.[2]?.value === thirdAxisItem.value)
      : true;

    return <div className="entity-view">
      <Paper componentId="risk-matrix-paper" className={undefined}>
        {this.props.shouldDisplayModal
          ? <PromptConfirmation
            title={translation.cancel_changes_button}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel}
          />
          : null}
        {this.state.shouldDisplayInternalModal
          ? <PromptConfirmation
            title={translation.cancel_changes_button}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.onInternalModalContinue}
            onCancel={this.onInternalModalCancel}
          />
          : null
        }
        <RiskMatrixStickyHeader
          axes={axes}
          cancel={this.cancelChanges}
          save={this.saveChanges}
          translation={translation}
          breadcrumbs={topToolbar}
          readOnly={readOnly}
          disabled={!this.isAnyCellModified() || this.isRiskMatrixFetching() || this.areNoTypesAvailable()}
        />
        <MainGridContainer>
          <RiskMatrixType
            options={riskMatrixTypeOptions}
            loadOptions={loadOptions}
            translation={translation}
            onChange={this.setType}
            value={type}
            fetching={fetchingRiskMatrixType}
          />
          {this.isRiskMatrixReady() && !this.isTypeChosen() ? <div className="col-md-12 col-lg-7">
            <p className={`no-content ${styles.noContent}`}>{translation.choosing_type_is_required}</p>
          </div> : null}
          {
            this.isTypeChosen()
              ? <>
                <CaseTypes
                  fetching={fetchingCaseTypes}
                  caseTypes={caseTypes}
                  translation={translation}
                  show={!shouldHideCaseTypes}
                />
                {thirdAxisItem ? <ThirdAxisItemPicker
                  setThirdAxisItem={this.setThirdAxisItem}
                  thirdAxisItem={thirdAxisItem}
                  options={axes?.[2]?.items}
                  label={{ text: thirdAxisLabel, visible: !!thirdAxisLabel }}
                  info={{
                    text: this.isMobile(width)
                      ? translation.third_axis_label_info_text_mobile : translation.third_axis_label_info_text, visible: !readOnly
                  }}
                /> : null}
              </>
              : null
          }
          {
            this.isRiskMatrixReady() && this.isTypeChosen()
              ? <>
                <div className={styles.inputLabel}>
                  <InputLabel text={translation.risk_matrix} />
                  <InfoText
                    text={this.isMobile(width) ? translation.risk_matrix_label_info_text_mobile : translation.risk_matrix_label_info_text}
                    visible={!readOnly} />
                </div>
                <div className={`row ${styles.matrixPanelContainer}`}>
                  <div className={styles.matrixPanel}>
                    {areThirdAxisItemCellsMissing
                      ? <div>
                        <p className={`no-content ${styles.noContent}`}>{translation.no_risk_matrix_data_for_type}</p>
                      </div>
                      : <>
                        <div className="col-md-12 col-lg-7">
                          <Matrix
                            riskMatrixDefinition={riskMatrixDefinition}
                            axes={axes}
                            onSelectedCell={this.chooseCell}
                            translation={translation}
                            thirdAxisItem={thirdAxisItem}
                          />
                        </div>
                        <div className="col-md-12 col-lg-5">
                          <ControlPanel
                            translation={translation}
                            axes={axes}
                            cell={selectedCell}
                            updateSelectedCellRiskArea={this.updateSelectedCellRiskArea}
                            updateSelectedCellRiskWeight={this.updateSelectedCellRiskWeight}
                            riskAreas={riskAreas}
                            readOnly={readOnly}
                            onClose={clearSelectedCell}
                          />
                        </div>
                      </>}
                  </div>
                </div>
              </>
              : this.isRiskMatrixFetching()
                ? <ElementLoader />
                : null}
        </MainGridContainer>
      </Paper >
    </div >;
  }
}

class RiskMatrixAdministrationComponentResizeWrapped extends Component<RiskMatrixAdministrationProps, RiskMatrixAdministrationState> {
  public static statePath: string = STATE.riskMatrix;

  public render() {
    return <ResizeWrapper>
      <RiskMatrixAdministrationComponent {...this.props} />
    </ResizeWrapper>;
  }
}

const selectors = {
  shouldPrompt: (state: RiskMatrixState) => state.modifiedCells.length !== 0 &&
    state.modifiedCells.find(modifiedCell => !state.cells.find(cell => deepEquals(cell, modifiedCell)))
};

const container = containerFactory(
  withOptions(withPrompt(RiskMatrixAdministrationComponentResizeWrapped) as any),
  actionCreators,
  selectors,
  riskMatrixTranslation
);

export { container as RiskMatrixAdministration };
